import { BadgeCheck, Layers, Sparkles, MapPin, Building2, Landmark } from "lucide-react";
import { WHY_US, AREAS_SCOTLAND, AREAS_ENGLAND, STEPS } from "./data";
import { scrollToId } from "./hooks";

const WHY_ICONS = [BadgeCheck, Layers, Sparkles, MapPin, Building2, Landmark];

export function WhyUs() {
  return (
    <section id="why-us" className="bg-[#182330] text-[#eef4f8] py-24 md:py-32 border-b border-[#213042]" data-testid="why-us-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-2xl mb-16 reveal">
          <span className="font-mono-code text-xs text-[#00c2a8] uppercase tracking-widest block mb-2">// WHY CHOOSE US</span>
          <h2 className="font-mono-code text-[clamp(28px,3.8vw,48px)] font-bold uppercase text-white mb-4 leading-tight">
            Specialists, Not Generalists
          </h2>
          <p className="text-sm text-[#a2b8cc] leading-relaxed">
            Silicone sealing and mastic work is all we do — and we do it to a professional standard on every job,
            whatever the scale.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {WHY_US.map((w, i) => {
            const Icon = WHY_ICONS[i];
            return (
              <div
                key={w.title}
                className="reveal bg-[#111923] border border-[#213042] p-6 rounded hover:border-[#00c2a8]/60 transition-colors"
                style={{ transitionDelay: `${(i % 3) * 60}ms` }}
                data-testid={`why-card-${i + 1}`}
              >
                <Icon className="w-6 h-6 text-[#00c2a8] mb-4" aria-hidden="true" />
                <h3 className="font-mono-code text-base font-bold uppercase text-white mb-2 leading-snug">{w.title}</h3>
                <p className="text-xs text-[#7d96ac] leading-relaxed">{w.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function Areas() {
  return (
    <section id="territory" className="bg-[#111923] text-[#eef4f8] py-24 md:py-32 border-b border-[#213042]" data-testid="areas-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 reveal">
            <span className="font-mono-code text-xs text-[#00c2a8] uppercase tracking-widest block mb-2">// COVERAGE</span>
            <h2 className="font-mono-code text-[clamp(26px,3.4vw,44px)] font-bold uppercase text-white mb-6 leading-tight">
              From Durham To John o&apos; Groats — We&apos;ve Got You Covered
            </h2>
            <p className="text-sm text-[#a2b8cc] leading-relaxed mb-4">
              We are based near Edinburgh and provide professional silicone sealing and mastic services throughout
              Scotland and North East England.
            </p>
            <p className="text-sm text-[#a2b8cc] leading-relaxed mb-8">
              Larger commercial and industrial contracts can justify longer-distance travel, while smaller domestic
              jobs may be subject to availability and travel requirements — get in touch and we&apos;ll confirm
              coverage for your job.
            </p>

            <div className="relative border border-[#213042] rounded overflow-hidden bg-[#182330]" data-testid="areas-map">
              <iframe
                title="Service area map — Scotland and North East England"
                src="https://www.openstreetmap.org/export/embed.html?bbox=-3.6%2C54.6%2C-1.2%2C56.4&layer=mapnik&marker=55.9533%2C-3.1883"
                className="w-full h-[300px] grayscale invert-[0.92] contrast-[0.9]"
                loading="lazy"
              />
              <div className="absolute bottom-0 inset-x-0 bg-[#111923]/85 backdrop-blur px-4 py-2.5 font-mono-code text-[10px] uppercase tracking-widest text-[#00c2a8]">
                Based near Edinburgh &bull; Mobile teams cover the full territory
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            <div className="bg-[#182330] border border-[#213042] rounded p-8 mb-6">
              <h3 className="font-mono-code text-xs text-[#00c2a8] uppercase tracking-widest mb-4">// SCOTLAND</h3>
              <div className="flex flex-wrap gap-2.5" data-testid="areas-scotland">
                {AREAS_SCOTLAND.map((a) => (
                  <span key={a} className="font-mono-code text-[11px] text-white border border-[#30455e] rounded px-3 py-1.5">
                    {a}
                  </span>
                ))}
              </div>
            </div>
            <div className="bg-[#182330] border border-[#213042] rounded p-8 mb-6">
              <h3 className="font-mono-code text-xs text-[#00c2a8] uppercase tracking-widest mb-4">// NORTH EAST ENGLAND</h3>
              <div className="flex flex-wrap gap-2.5" data-testid="areas-england">
                {AREAS_ENGLAND.map((a) => (
                  <span key={a} className="font-mono-code text-[11px] text-white border border-[#30455e] rounded px-3 py-1.5">
                    {a}
                  </span>
                ))}
              </div>
            </div>
            <button
              onClick={() => scrollToId("contact")}
              className="font-mono-code bg-[#00c2a8] text-[#111923] font-bold text-xs px-8 py-4 rounded uppercase tracking-wider hover:bg-white transition-colors"
              data-testid="areas-quote-btn"
            >
              Check Coverage For Your Job
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export function HowItWorks() {
  return (
    <section id="how-it-works" className="bg-[#182330] text-[#eef4f8] py-24 md:py-32 border-b border-[#213042]" data-testid="how-it-works-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-2xl mb-16 reveal">
          <span className="font-mono-code text-xs text-[#00c2a8] uppercase tracking-widest block mb-2">// PROCESS</span>
          <h2 className="font-mono-code text-[clamp(28px,3.8vw,48px)] font-bold uppercase text-white leading-tight">
            How It Works
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s, i) => (
            <div
              key={s.n}
              className="reveal bg-[#111923] border border-[#213042] p-6 rounded relative"
              style={{ transitionDelay: `${i * 70}ms` }}
              data-testid={`step-card-${i + 1}`}
            >
              <span className="font-mono-code text-4xl font-bold text-[#00c2a8] block mb-4">{s.n}.</span>
              <h3 className="font-mono-code text-sm font-bold uppercase text-white mb-2 tracking-wider">{s.title}</h3>
              <p className="text-xs text-[#7d96ac] leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center reveal">
          <button
            onClick={() => scrollToId("contact")}
            className="font-mono-code bg-[#00c2a8] text-[#111923] font-bold text-xs px-8 py-4 rounded uppercase tracking-wider hover:bg-white transition-colors"
            data-testid="how-quote-btn"
          >
            Start Step One — Get In Touch
          </button>
        </div>
      </div>
    </section>
  );
}
